/**
 * API Nomenclatura - Single Source of Truth
 * Constantes para nombres de campos en API REST y SSE
 *
 * ARQUITECTURA MULTI-JUEGO (2026):
 *   - El primer juego implementado es LotoWheel (antes llamado "juego" o "ruleta")
 *   - Los campos legacy (juegoId, numSorteo) se mantienen para compatibilidad
 *   - Los nuevos campos (sorteoConfigId, drawSequenceNumber) son preferidos
 *
 * Uso:
 * - ESM: import { KEYS, EVENTS, extractSorteando } from './api-nomenclatura.js'
 * - Backend: const { KEYS, EVENTS } = require('./api-nomenclatura');
 * - Browser: <script src="api-nomenclatura.js"></script> -> window.ApiNomenclature
 *
 * Principios DDD - Lenguaje Ubiquo:
 * - sorteoEnCurso: el sorteo activo (aceptando apuestas o girando)
 * - sorteoSiguiente: el proximo sorteo (para transicion fluida)
 * - girando: si la rueda esta girando, contiene el resultado
 * - resultadoSorteo: numero ganador (0-36 en LotoWheel)
 */

// ═══════════════════════════════════════════════════════════════════════════
// CLAVES DE CAMPOS (para payloads JSON)
// ═══════════════════════════════════════════════════════════════════════════

export const KEYS = {
    // Campos del endpoint /estado
    ESTADO: {
        sorteoEnCurso: 'sorteoEnCurso',
        sorteoSiguiente: 'sorteoSiguiente',
        girando: 'girando',
        enPausa: 'enPausa',
        historialResultados: 'historialResultados',
        pozo: 'pozo',
        ts: 'ts',
    },

    // Campos de objetos sorteo
    SORTEO: {
        num: 'num',
        fecha: 'fecha',
        resultadoSorteo: 'resultadoSorteo',
    },

    // Campos de eventos SSE
    EVENT_PAYLOAD: {
        numSorteo: 'numSorteo',
        resultadoSorteo: 'resultadoSorteo',
        sorteoSiguiente: 'sorteoSiguiente',
        pozo: 'pozo',
        ts: 'ts',
        fechaResultado: 'fechaResultado',
        sorteoConfigId: 'sorteoConfigId',
        drawId: 'drawId',
        // Lightning del sorteo vivo: sectores afortunados de ESTA ronda. Publico
        // (describe la rueda comun, no al jugador) y opcional (ausente = rueda
        // sin Lightning). Se genera despues del cierre de apuestas.
        afortunados: 'afortunados',
        // Campos privados (solo para apostadores)
        saldo: 'saldo',
        ganancia: 'ganancia',
        premios: 'premios',
    },

    // Campos de pozo (endpoint /pozo y eventos)
    POZO: {
        pozoId: 'pozoId',
        // El juego del producto al que va esta copia del evento (ver `eventoDePozoEsDelJuego`).
        juegoDelCanal: 'juegoDelCanal',
        actual: 'actual',
        umbral: 'umbral',
        base: 'base',
        porcentaje: 'porcentaje',
        moneda: 'moneda',
        minMult: 'min_mult',
        maxMult: 'max_mult',
        progreso: 'progreso',
        premio: 'premio',
        nuevoValor: 'nuevoValor',
        premioFormateado: 'premioFormateado',
        aporteCalculado: 'aporte_calculado',
    },

    // Campos de apuestas (endpoints /apuestas)
    // NOTA: Nuevos nombres para arquitectura multi-juego
    APUESTA: {
        sorteoConfigId: 'sorteo_config_id',
        drawSequenceNumber: 'draw_sequence_number',
        nodoId: 'nodo_id',
        juegoId: 'juego_id',
        clienteId: 'cliente_id',
        tvId: 'tv_id',
        tipo: 'tipo',
        valor: 'valor',
        monto: 'monto',
        pos: 'pos',
        apuestas: 'apuestas',
        resumen: 'resumen',
        operationId: 'operationId',
        // Sorteo que el cliente declara para su operación: la ata a ESE sorteo.
        roundId: 'roundId',
        productKind: 'productKind',
    },

    // Contrato común de play/status para juegos instantáneos. El outcome sólo
    // existe en INSTANT_SETTLED; PENDING nunca transporta resultado parcial.
    INSTANT_PLAY: {
        kind: 'kind',
        operationId: 'operationId',
        productId: 'productId',
        // El producto se identifica por `(gameTypeId, productId)`: los ids de
        // configuración viven en la tabla de cada juego, así que el mismo
        // número puede existir en dos catálogos. Opcional en el wire por
        // compatibilidad — sin él el backend lo infiere solo si no hay
        // ambigüedad. El cliente NUNCA envía `gameCode` ni tabla: el servidor
        // deriva el juego del game type autorizado.
        gameTypeId: 'gameTypeId',
        // Comando propio del juego. Viaja OPACO: ninguna capa común lo
        // interpreta. Excluyente con `apuestas` (ver la ruta INSTANT).
        commandPayload: 'commandPayload',
        roundId: 'roundId',
        playCode: 'playCode',
        recovery: 'recovery',
        outcome: 'outcome',
        balanceMinor: 'balanceMinor',
        lineas: 'lineas',
        plays: 'plays',
        betAmount: 'betAmount',
        createdAt: 'createdAt',
    },

    // Operación administrativa de rounds instantáneos no terminales. El
    // operador aporta evidencia para reintentar la MISMA transacción; nunca
    // elige el estado financiero resultante.
    INSTANT_RECONCILIATION: {
        rounds: 'rounds_instantaneos',
        detail: 'rounds_instantaneos_detalle',
        gameRoundReference: 'gameRoundReference',
        nodeId: 'nodo_id',
        gameCode: 'game_code',
        phase: 'fase',
        operationId: 'operationId',
        justification: 'justification',
        state: 'estado',
        roundState: 'round_estado',
        ageSeconds: 'antiguedad_segundos',
        attempts: 'intentos',
        lastError: 'last_error',
        createdAt: 'created_at',
        updatedAt: 'updated_at',
        evidenceType: 'evidenceType',
        replay: 'replay',
        // Incidente derivado: el socio RECHAZO la devolucion de un debito que
        // ya cobro y cuya apuesta nunca se registro. No es un estado nuevo —
        // se deriva de las filas existentes — pero el dinero sigue con el
        // socio y alguien tiene que actuar a mano.
        retainedRefunds: 'devoluciones_retenidas',
        retainedRefundsDetail: 'devoluciones_retenidas_detalle',
        amountCents: 'monto_centavos',
        refundTransactionId: 'refund_transaction_id',
        partnerCode: 'partner_code',
    },

    // Campos de respuestas genericas
    RESPONSE: {
        ok: 'ok',
        error: 'error',
        message: 'message',
        code: 'code',
        data: 'data',
    },

    // Códigos estables de error que viajan en `response.code`. SSOT compartido
    // entre backend (origen) y clientes (consumers). Snake_case por convención
    // del wire. Agregar code nuevo → agregar entrada acá + spec en el cliente.
    ERROR_CODES: Object.freeze({
        // Money-path
        SALDO_INSUFICIENTE: 'saldo_insuficiente',
        SALDO_OPERATIVO_INSUFICIENTE: 'saldo_operativo_insuficiente',
        SALDO_REAL_INSUFICIENTE: 'saldo_real_insuficiente',
        STOCK_CUPONES_INSUFICIENTE: 'stock_cupones_insuficiente',
        DESTINO_CAMBIO: 'destino_cambio',
        DESTINO_INVALIDO: 'destino_invalido',
        DESTINO_VENCIDO: 'destino_vencido',
        MONEDA_INCOMPATIBLE: 'moneda_incompatible',
        CAPACIDAD_DENEGADA: 'capacidad_denegada',
        IDEMPOTENCY_REQUERIDA: 'idempotency_requerida',
        // La wallet del socio RECHAZÓ la operación por contrato. No es un fallo
        // nuestro: nuestro servidor llamó, el socio respondió, y su respuesta
        // fue «no». Sin este código todo salía como 500 «Error registrando
        // apuestas» y el integrador buscaba el fallo en nuestro lado.
        WALLET_EXTERNA_RECHAZO: 'wallet_externa_rechazo',
        // Fallo NUESTRO. Se declara para que el integrador pueda distinguirlo
        // del anterior por código y no por el texto del mensaje.
        ERROR_INTERNO: 'error_interno',

        // Auth / login
        MISSING_FIELDS: 'missing_fields',
        CREDENCIALES_INVALIDAS: 'credenciales_invalidas',
        USUARIO_DESACTIVADO: 'usuario_desactivado',
        USUARIO_SIN_NODO: 'usuario_sin_nodo',
        SIN_AUTORIDAD: 'sin_autoridad',
        // Login POS: el usuario existe y tiene autoridad sobre la caja (subárbol), pero
        // su rol no es 'cajero' — un operador/representante puede administrar la red pero
        // no debe operar la caja física (sin wallet/saldo operativo de cajero ahí).
        ROL_NO_CAJERO: 'rol_no_cajero',
        // Onboarding universal (v77): la cuenta existe pero nació sin contraseña
        // (password_hash NULL) — el front deriva al flujo "creá tu contraseña".
        CUENTA_SIN_CONTRASENA: 'cuenta_sin_contrasena',
        // Activación de cuenta de caja web (cashier-activacion-web):
        YA_TIENE_CORREO: 'ya_tiene_correo', // variante (b): no corresponde pedir código
        EMAIL_EN_USO: 'email_en_uso', // el correo pertenece a otro usuario
        YA_ACTIVADA: 'ya_activada', // la contraseña ya fue fijada (gate atómico rebotó)
        CODIGO_CONSUMIDO: 'codigo_consumido', // el código ya fue usado — re-solicitar
        MULTIPLES_CUENTAS: 'multiples_cuentas', // 2+ cuentas en la caja — el front pide el correo para desambiguar
    }),

    // Campos de saldo (evento saldo:update)
    SALDO: {
        clienteId: 'cliente_id',
        nodoId: 'nodo_id',
        nuevoSaldo: 'nuevo_saldo',
        nuevoSaldoReal: 'nuevo_saldo_real',
        nuevoSaldoBonus: 'nuevo_saldo_bonus',
        tipo: 'tipo',
        monto: 'monto',
    },

    // Campos de resultados de apuestas
    RESULTADO_APUESTA: {
        montoTotal: 'montoTotal',
        saldoRestante: 'saldoRestante',
        numApuestas: 'numApuestas',
    },

    // Campos de premio individual (sub-objeto de premios[])
    PREMIO: {
        tipo: 'tipo',
        valor: 'valor',
        apostado: 'apostado',
        premio: 'premio',
    },
};

// ═══════════════════════════════════════════════════════════════════════════
// NOMBRES DE EVENTOS SSE
// ═══════════════════════════════════════════════════════════════════════════

export const EVENTS = {
    // Sorteo
    SORTEO_SORTEANDO: 'sorteo:sorteando',
    SORTEO_RESULTADO: 'sorteo:resultado',
    SORTEO_AVISO_CIERRE: 'sorteo:aviso_cierre',
    SORTEO_CIERRE_APUESTAS: 'sorteo:cierre_apuestas',
    SORTEO_SYNC: 'sorteo:sync',
    // Red Door: apertura de la ventana viaja DENTRO del payload de sorteo:resultado (campo
    // `bonus`, SOLO metadata — los valores sellados jamás viajan antes del deadline). La
    // revelación (valores + resultados por jugador) es este evento, emitido al pagar tx#2.
    SORTEO_BONUS_REVELACION: 'sorteo:bonus_revelacion',

    // Pozo
    POZO_UPDATE: 'pozo:update',
    POZO_WINNER: 'pozo:winner',
    POZO_CONFIG_CHANGED: 'pozo:config-changed',

    // Saldo
    SALDO_UPDATE: 'saldo:update',
    SALDO_OPERATIVO_UPDATE: 'saldo_operativo:update',
    // La caja movió los fondos de una terminal (recarga o cobro). Va al canal
    // `device:<serial>` de ESA terminal, nunca a uno del nodo. NO transporta el
    // importe a propósito: solo avisa "tus fondos cambiaron", y la terminal los
    // relee de /api/vlt/game/funds, que es la autoridad. Así un evento perdido,
    // duplicado o desordenado retrasa la pantalla pero no puede falsearla.
    VLT_FONDOS_CAMBIARON: 'vlt:fondos_cambiaron',

    // Dispositivos
    DISPOSITIVO_AUTHORIZED: 'dispositivo:authorized',
    DISPOSITIVO_CONFIG_CHANGED: 'dispositivo:config-changed',
    DISPOSITIVO_REVOKED: 'dispositivo:revoked',

    // Nodos
    NODO_CONFIG_CHANGED: 'nodo:config-changed',

    // Alertas
    ALERTA: 'alerta',
    ALERTA_FRAUDE: 'alerta:fraude',

    // Session (PIN-based auth)
    SESSION_CLOSE: 'session:close',
    // Aviso de convergencia, nunca autoridad: el backend ya cerró el turno
    // anterior antes de emitirlo. Customer escucha por user:<tel> y VLT por
    // device:<serial>; ambos revalidan contra su endpoint autoritativo.
    PLAYER_TURN_REPLACED: 'player:turn-replaced',

    // Retiro Authorization (Cashier ↔ Client)
    RETIRO_SOLICITUD: 'retiro:solicitud', // Backend → Cliente PWA: "¿aprobar retiro?"
    RETIRO_RESPUESTA: 'retiro:respuesta', // Backend → Cashier (+ Cliente para dismiss)

    // Compliance (validación cajero PIN one-time, §8 roadmap post-v1.0)
    COMPLIANCE_VALIDACION_PENDIENTE: 'compliance:validacion_pendiente',
    COMPLIANCE_VALIDACION_RESUELTA: 'compliance:validacion_resuelta',

    // Pasarela (pagos PIX, etc.)
    PASARELA_RECARGA_CONFIRMADA: 'pasarela:recarga_confirmada',
    // Pago confirmado pero NO acreditado: el CPF del pagador difiere del titular
    // de la cuenta (titularidad PIX). El operador lo libera o devuelve a mano.
    PASARELA_RECARGA_RETENIDA: 'pasarela:recarga_retenida',
    PASARELA_RETIRO_CONFIRMADO: 'pasarela:retiro_confirmado',
    PASARELA_RETIRO_FALLIDO: 'pasarela:retiro_fallido',

    // Cobro en efectivo de una VLT (hand pay). Viajan al canal privado del
    // cajero (`cashier:<dispositivoId>`) para que la caja muestre el pendiente
    // sin refrescar, y al canal del dispositivo (`device:<serial>`) para que la
    // terminal salga de su pantalla de bloqueo al resolverse.
    // Payload: { quoteRef, amountMinor, currency, terminal: {id, nombre, serial} }.
    VLT_COBRO_SOLICITADO: 'vlt:cobro_solicitado',
    // El cajero entregó el efectivo. Payload: { quoteRef, amountMinor, currency }.
    VLT_COBRO_PAGADO: 'vlt:cobro_pagado',
    // El cajero canceló: el saldo vuelve a estar jugable en la terminal.
    // Payload: { quoteRef }.
    VLT_COBRO_CANCELADO: 'vlt:cobro_cancelado',

    // Marketing (motor de promociones → canal privado user:<telefono> del jugador)
    // Recompensa tipo `notificacion` de una promo (bonus de registro, cashback, etc.).
    // Payload: { tipo, mensaje, codigoPromo, nombrePromo } — mensaje ya viene en el
    // idioma de la promo, listo para mostrar. codigoPromo/nombrePromo permiten que
    // la presentación evolucione (toast hoy, modal de recompensa mañana) sin tocar
    // este contrato.
    MARKETING_NOTIFICACION: 'marketing:notificacion',
    // Invalida el snapshot durable de recompensas; no transporta ni confirma
    // la recompensa. Payload: { rewardEventIds: string[] }.
    MARKETING_RECOMPENSA_PENDIENTE: 'marketing:recompensa_pendiente',
    // Invalida la proyección personal (p. ej. wagering completado/expirado).
    // No transporta autoridad ni progreso: el cliente vuelve a leer snapshot.
    MARKETING_ESTADOS_RECOMPENSA_INVALIDADOS: 'marketing:estados_recompensa_invalidados',

    // Config
    CONFIG_UPDATE: 'config:update',
    CONFIG_HASHES: 'config:hashes',

    // POS Fleet (push admin → device cashier)
    // pos:apk-update-disponible — admin promovió release nueva, el POS dispara
    // checkForUpdate() inmediato (sin esperar al poll de fleet de cada 15 min).
    // Crítico cuando hay un fix urgente en producción.
    POS_APK_UPDATE_DISPONIBLE: 'pos:apk-update-disponible',
    // pos:fleet-changed — admin cambió feature flag o policy del fleet, el POS
    // dispara FleetManager.refreshConfig() inmediato. Útil para apagar
    // pasarelaPix por incidente, ajustar lockTimeoutSeconds, etc.
    POS_FLEET_CHANGED: 'pos:fleet-changed',
    // pos:fleet-refresh-now — admin pidió data fresca de un POS específico.
    // El POS dispara sendHeartbeat() + TelemetryBuffer.flush() inmediato.
    // Útil para diagnóstico en vivo desde modal Fleet info del admin.
    POS_FLEET_REFRESH_NOW: 'pos:fleet-refresh-now',

    // Tick (heartbeat)
    TICK: 'tick',
};

// ═══════════════════════════════════════════════════════════════════════════
// TV FLEET — sub-acciones del comando 'tv:comando' (push admin → TV kiosko)
// ═══════════════════════════════════════════════════════════════════════════
// El POS abre su propio SSE y recibe eventos pos:* dedicados. La TV NO: la PWA
// front-tv ya tiene SSE vivo al backend y un bridge loopback a la APK. Por eso
// el push de Fleet TV reusa el comando 'tv:comando' existente — la PWA lo
// recibe y lo delega a la APK por el bridge. Estas son las 3 sub-acciones
// nativas (las consume la APK, no la PWA sola):
//   FORZAR_UPDATE   — admin promovió release nueva → APK dispara checkForUpdate()
//                     inmediato sin esperar el poll de fleet.
//   REFRESCAR_CONFIG — admin cambió config del fleet → APK re-fetch del config.
//   REFRESCAR_FLEET  — admin pidió data fresca de una TV → APK manda heartbeat
//                      + flush de telemetría ya (diagnóstico en vivo).
//   LIMPIAR_WEBVIEW  — fuerza la limpieza del Service Worker + caches del
//                      WebView/Chromium. Acelera la convergencia al SW nuevo
//                      en TVs que aún cargan (la limpieza por sello de versión
//                      del APK ya cubre el fleet completo; esto es el empujón
//                      inmediato del admin).
export const TV_FLEET_ACTIONS = {
    FORZAR_UPDATE: 'fleet_forzar_update',
    REFRESCAR_CONFIG: 'fleet_refrescar_config',
    REFRESCAR_FLEET: 'fleet_refrescar_fleet',
    LIMPIAR_WEBVIEW: 'fleet_limpiar_webview',
};

// ═══════════════════════════════════════════════════════════════════════════
// CANALES SSE (para broadcaster.emit)
// ═══════════════════════════════════════════════════════════════════════════

export const CHANNELS = {
    ADMIN_ALERTS: 'admin:alerts',
    SETUP: 'setup',
    USER_PREFIX: 'user:',
    TV_PREFIX: 'tv:',
    CASHIER_PREFIX: 'cashier:',
};

// ═══════════════════════════════════════════════════════════════════════════
// IDIOMAS SOPORTADOS — SSOT para todo el sistema
// ═══════════════════════════════════════════════════════════════════════════

export const IDIOMAS_SOPORTADOS = {
    ES: 'es',
    EN: 'en',
    PT: 'pt',
    RU: 'ru',
    DEFAULT: 'es',
    LISTA: ['es', 'en', 'pt', 'ru'],
    NOMBRES: {
        es: 'Español',
        en: 'English',
        pt: 'Português',
        ru: 'Русский',
    },
};

// ═══════════════════════════════════════════════════════════════════════════
// RETIRO AUTHORIZATION — Constantes del sistema de autorización de retiros
// ═══════════════════════════════════════════════════════════════════════════

export const RETIRO_AUTH = {
    SOLICITUD_TTL_MS: 120_000, // 2 minutos para aprobar/rechazar
    STATUS_PENDING: 'pending',
    // Estado transitorio in-memory: se fija SINCRÓNICAMENTE al empezar a resolver por PIN,
    // antes del `await bcrypt.compare`, para que dos requests concurrentes no pasen ambas el
    // check de PENDING y ejecuten el retiro dos veces (TOCTOU → doble débito). Nunca se
    // persiste en DB.
    STATUS_RESOLVING: 'resolving',
    STATUS_APPROVED: 'approved',
    STATUS_REJECTED: 'rejected',
    STATUS_EXPIRED: 'expired',
    // Terminal por decisión del cajero: cerró el modal antes de que el cliente
    // autorizara. Libera al cliente en el acto — sin esto la solicitud seguía
    // PENDING sus 2 minutos y el siguiente retiro del mismo jugador chocaba
    // contra el guard de duplicados.
    STATUS_CANCELLED: 'cancelled',
    RESOLUTION_PIN: 'pin', // Aprobado via PIN en dispositivo cajero
    RESOLUTION_PWA: 'pwa', // Aprobado via app del cliente
    RESOLUTION_CASHIER_CANCELLED: 'cashier_cancelled', // Cancelado desde la caja que la creó
    // Código estable del intento duplicado. Es un conflicto de negocio (409),
    // no un fallo del servidor: el 500 genérico que devolvía antes escondía la
    // causa y hacía irreproducible el diagnóstico.
    ERROR_SOLICITUD_PENDIENTE: 'RETIRO_SOLICITUD_PENDIENTE',
};

// ═══════════════════════════════════════════════════════════════════════════
// CANAL ENTREGA COMPROBANTE — Cómo el cajero entrega el comprobante al cliente
// Opcional en body de operaciones que emiten comprobante (hoy: depósito en
// /cashier/creditos). Persistido en audit.extra.canalEntrega para trazabilidad
// + reporting futuro (ej. "qué % de clientes pide WhatsApp vs impresión").
// ═══════════════════════════════════════════════════════════════════════════

export const CANAL_ENTREGA_COMPROBANTE = {
    IMPRESION: 'impresion', // Ticket físico impreso en térmica del POS
    WHATSAPP: 'whatsapp', // Link o imagen enviada por WhatsApp al cliente
    COPIA: 'copia', // Copia digital (correo, link, PDF descargable)
    FOTO_PANTALLA: 'foto_pantalla', // Cliente fotografía la pantalla del cajero
};

/** Set con los valores válidos — útil para validación fail-fast en boundary. */
export const CANAL_ENTREGA_COMPROBANTE_VALORES = new Set(Object.values(CANAL_ENTREGA_COMPROBANTE));

// ═══════════════════════════════════════════════════════════════════════════
// ENDPOINTS — Rutas API usadas en 2+ fronts (SSOT cross-project)
// Solo rutas compartidas entre front-customer, front-cashier, front-tv, front-admin.
// Rutas single-project se quedan en su propio api-client.
// ═══════════════════════════════════════════════════════════════════════════

export const ENDPOINTS = {
    PUBLIC_BRANDING: '/api/public/branding',
    DISPOSITIVOS_SETUP: '/api/dispositivos/setup',
    DISPOSITIVOS_AUTH: '/api/dispositivos/auth',
    DISPOSITIVOS_CLAIM_VALE: '/api/dispositivos/claim-vale',
    // Caja web: canjea el link de acceso de un solo uso por el device-token.
    CASHIER_WEB_CANJEAR_ACCESO: '/api/cashier/web/canjear-acceso',
    LOTOWHEEL_CONFIGS: '/api/lotowheel/configs',
    ADMIN_LOGOUT: '/api/admin/logout',
    ADMIN_CONFORMIDAD_VOUCHER: '/api/admin/conformidad/voucher',
    ADMIN_CONFORMIDAD_PENDIENTES: '/api/admin/conformidad/pendientes',
    PASARELA_CONFIG: '/api/pasarela/config',
    INSTANT_PLAY: '/api/games/instant/play',
    INSTANT_PLAYS: '/api/games/instant/plays',
    INSTANT_OPERATIONS: '/api/games/instant/operations',
    INSTANT_HISTORY: '/api/games/instant/history',
};

export const INSTANT_ENDPOINTS = Object.freeze({
    CUSTOMER: '/api/games/instant',
    VLT_PLAYER: '/api/vlt/player/game/instant',
    VLT_TERMINAL: '/api/vlt/game/instant',
    B2B_PLAYER: '/api/b2b/player/game/instant',
});

export const INSTANT_RESULT_KIND = Object.freeze({
    SETTLED: 'INSTANT_SETTLED',
    PENDING: 'PENDING',
    REJECTED: 'REJECTED',
    HISTORY: 'INSTANT_HISTORY',
    // Los pozos comunes suscritos a un producto INSTANT, con su saldo vigente
    // y solo campos públicos (nunca umbral, reserva ni overflow).
    POOLS: 'INSTANT_POOLS',
    // La partida abierta del titular en un producto multironda (GET …/open-round): recuperar sin pista local.
    OPEN_ROUND: 'INSTANT_OPEN_ROUND',
});

// ═══════════════════════════════════════════════════════════════════════════
// PARÁMETROS URL — SSOT para query strings en todos los fronts
// ═══════════════════════════════════════════════════════════════════════════

export const URL_PARAMS = {
    // front-customer: login
    PHONE: 'phone',
    CODE: 'code',
    SORTEO: 'sorteo',

    // front-customer: auth externa (seamless wallet)
    AUTH_TOKEN: 'authToken',
    NODO_ID: 'nodoId',
    LOBBY_URL: 'lobbyUrl',
    LANG: 'lang',

    // front-customer: PIN reset
    RESET: 'reset',

    // front-customer: landing de bienvenida (?r=código de referido, ?pv=nodo)
    REFERIDO: 'r',
    PUNTO_VENTA: 'pv',

    // front-customer: canje de código (?codigo=…)
    CANJE_CODIGO: 'codigo',

    // front-cashier + front-tv: setup dispositivo
    RED: 'red',

    // dev-only
    DEBUG: 'debug',
    DEBUG_COPY: 'debugcopy',
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS PARA CONSTRUIR PAYLOADS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Construye objeto sorteoSiguiente estandar
 * @param {number} num - Numero del sorteo
 * @param {number} fecha - Timestamp en ms (fechaResultado del siguiente sorteo)
 * @param {number} [fechaCierre] - Timestamp en ms del cierre del siguiente sorteo.
 *   Opcional para retro-compat — solo se incluye en el objeto si se pasa. Lo
 *   usa el cliente para arrancar countdown sin esperar al snapshot post-cierre.
 */
export function buildSorteoSiguiente(num, fecha, fechaCierre = undefined) {
    const K = KEYS.SORTEO;
    const out = {
        [K.num]: num,
        [K.fecha]: fecha,
    };
    if (fechaCierre !== undefined && fechaCierre !== null) {
        out.fechaCierre = fechaCierre;
    }
    return out;
}

/**
 * Construye payload de sorteo:sorteando (publico).
 * Incluye fechaResultado (ts + spinDurationMs) para que el cliente calcule
 * la duración restante del spin sin depender de reloj local ni defaults.
 */
export function buildSorteandoPayload(
    numSorteo,
    resultadoSorteo,
    pozo,
    sorteoSiguienteNum,
    sorteoSiguienteFecha,
    ts,
    fechaResultado,
    afortunados = null
) {
    const K = KEYS.EVENT_PAYLOAD;
    const payload = {
        [K.numSorteo]: numSorteo,
        [K.resultadoSorteo]: resultadoSorteo,
        [K.pozo]: pozo,
        [K.sorteoSiguiente]: buildSorteoSiguiente(sorteoSiguienteNum, sorteoSiguienteFecha),
        [K.ts]: ts,
        [K.fechaResultado]: fechaResultado ?? null,
    };
    // Argumento dedicado y no `extraFields`: ese objeto se derrama a
    // `sorteo:resultado` y al aviso de cierre, donde los afortunados de la ronda
    // ya no significan nada. La clave se omite entera sin Lightning, para que el
    // payload de una rueda legacy quede byte a byte como antes.
    if (afortunados !== null && afortunados !== undefined) {
        payload[K.afortunados] = afortunados;
    }
    return payload;
}

/**
 * Construye payload de sorteo:resultado
 */
export function buildResultadoPayload(numSorteo, resultadoSorteo, sorteoSiguienteNum, sorteoSiguienteFecha, ts) {
    const K = KEYS.EVENT_PAYLOAD;
    return {
        [K.numSorteo]: numSorteo,
        [K.resultadoSorteo]: resultadoSorteo,
        [K.sorteoSiguiente]: buildSorteoSiguiente(sorteoSiguienteNum, sorteoSiguienteFecha),
        [K.ts]: ts,
    };
}

/**
 * Construye payload de sorteo:aviso_cierre
 * @param {number} numSorteo - Número del sorteo que está por cerrar
 * @param {number} ts - Timestamp del evento
 * @param {Object} extraFields - Campos adicionales (ej: drawId para virtuales)
 */
export function buildAvisoCierrePayload(numSorteo, ts, extraFields = {}) {
    const K = KEYS.EVENT_PAYLOAD;
    return {
        [K.numSorteo]: numSorteo,
        [K.ts]: ts,
        ...extraFields,
    };
}

/**
 * Construye payload de sorteo:cierre_apuestas
 * @param {number} numSorteo - Número del sorteo que cierra
 * @param {number} sorteoSiguienteNum - Número del siguiente sorteo
 * @param {number} sorteoSiguienteFecha - Timestamp del siguiente sorteo
 * @param {number} ts - Timestamp del evento
 * @param {Object} extraFields - Campos adicionales (ej: drawId para virtuales)
 */
export function buildCierreApuestasPayload(
    numSorteo,
    sorteoSiguienteNum,
    sorteoSiguienteFecha,
    ts,
    extraFields = {},
    sorteoSiguienteFechaCierre = undefined
) {
    const K = KEYS.EVENT_PAYLOAD;
    return {
        [K.numSorteo]: numSorteo,
        [K.sorteoSiguiente]: buildSorteoSiguiente(sorteoSiguienteNum, sorteoSiguienteFecha, sorteoSiguienteFechaCierre),
        [K.ts]: ts,
        ...extraFields,
    };
}

/**
 * Construye objeto pozo estandar
 */
export function buildPozoResponse(actual, umbral, base, porcentaje, moneda, minMult, maxMult) {
    const P = KEYS.POZO;
    return {
        [P.actual]: actual,
        [P.umbral]: umbral,
        [P.base]: base,
        [P.porcentaje]: porcentaje,
        [P.moneda]: moneda,
        [P.minMult]: minMult,
        [P.maxMult]: maxMult,
    };
}

/**
 * Construye respuesta generica OK
 */
export function buildOkResponse(data) {
    const R = KEYS.RESPONSE;
    return { [R.ok]: true, ...data };
}

/**
 * Construye respuesta de error fail-fast.
 *
 * @param {string} message — Mensaje genérico, sin números ni moneda. Fallback
 *   para clientes sin soporte de `code+data`. Obligatorio non-empty.
 * @param {{code: string, data?: object}} [extras] — Opcional. Si se pasa:
 *   - `code` (string, OBLIGATORIO): código snake_case del SSOT `KEYS.ERROR_CODES`.
 *   - `data` (object plano, opcional): datos crudos. Convención: campos de
 *     monto en `*_centavos` (long). Campo `moneda` (ISO) presente solo si
 *     se puede resolver confiablemente desde el handler.
 *
 * @throws {TypeError} si el contrato se viola. Fastify `setErrorHandler`
 *   (index.js) lo convierte en HTTP 500 estructurado. Intencional: prefiero
 *   500 ruidoso a `{error: undefined}` silencioso que confunde al cliente.
 * @returns {object} `{ error: string, code?: string, data?: object }`
 */
export function buildErrorResponse(message, extras) {
    if (typeof message !== 'string' || message.length === 0) {
        throw new TypeError('buildErrorResponse: message debe ser string non-empty');
    }
    const R = KEYS.RESPONSE;
    const out = { [R.error]: message };
    if (extras === undefined) return out;
    if (typeof extras !== 'object' || extras === null || Array.isArray(extras)) {
        throw new TypeError('buildErrorResponse: extras debe ser objeto plano');
    }
    if (typeof extras.code !== 'string' || extras.code.length === 0) {
        throw new TypeError('buildErrorResponse: extras.code es obligatorio (string non-empty) cuando se pasan extras');
    }
    out[R.code] = extras.code;
    if (extras.data !== undefined) {
        if (typeof extras.data !== 'object' || extras.data === null || Array.isArray(extras.data)) {
            throw new TypeError('buildErrorResponse: extras.data debe ser objeto plano');
        }
        out[R.data] = extras.data;
    }
    return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// EXTRACTORES FAIL-FAST (para handlers SSE y respuestas API)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Extrae datos de sorteo:sorteando con validacion fail-fast
 * @param {object} data - Payload del evento
 * @returns {object|null} { numSorteo, resultadoSorteo, pozo, sorteoSiguiente, ts } o null si invalido
 */
export function extractSorteando(data) {
    const KP = KEYS.EVENT_PAYLOAD;
    const KS = KEYS.SORTEO;

    const numSorteo = data[KP.numSorteo];
    const resultadoSorteo = data[KP.resultadoSorteo];
    const sorteoSiguiente = data[KP.sorteoSiguiente];

    // Fail-fast: campos requeridos
    if (resultadoSorteo === undefined) {
        console.error('[extractSorteando] FAIL: resultadoSorteo undefined');
        return null;
    }

    const numero = parseInt(resultadoSorteo, 10);
    if (isNaN(numero) || numero < 0) {
        console.error('[extractSorteando] FAIL: resultadoSorteo invalido:', resultadoSorteo);
        return null;
    }

    // Normalizar premios si existen
    const KPR = KEYS.PREMIO;
    const rawPremios = data[KP.premios];
    const premios = Array.isArray(rawPremios)
        ? rawPremios.map(p => ({
              tipo: p[KPR.tipo],
              valor: p[KPR.valor],
              apostado: p[KPR.apostado],
              premio: p[KPR.premio],
          }))
        : null;

    return {
        numSorteo: numSorteo,
        resultadoSorteo: numero,
        pozo: data[KP.pozo],
        sorteoSiguiente: sorteoSiguiente
            ? {
                  num: sorteoSiguiente[KS.num],
                  fecha: sorteoSiguiente[KS.fecha],
              }
            : null,
        ts: data[KP.ts],
        // Lightning: ausente o malformado => null (rueda sin Lightning). No se
        // valida el contenido aca; el productor ya lo normalizo en su frontera.
        afortunados: Array.isArray(data[KP.afortunados]) ? data[KP.afortunados] : null,
        // Campos privados (pueden no existir en mensaje publico)
        saldo: data[KP.saldo],
        ganancia: data[KP.ganancia],
        premios: premios,
    };
}

/**
 * Extrae datos de sorteo:resultado con validacion fail-fast
 * @param {object} data - Payload del evento
 * @returns {object|null} { numSorteo, resultadoSorteo, sorteoSiguiente, ts } o null si invalido
 */
export function extractResultado(data) {
    const KP = KEYS.EVENT_PAYLOAD;
    const KS = KEYS.SORTEO;

    const resultadoSorteo = data[KP.resultadoSorteo];
    const sorteoSiguiente = data[KP.sorteoSiguiente];

    // Fail-fast: campos requeridos
    if (resultadoSorteo === undefined) {
        console.error('[extractResultado] FAIL: resultadoSorteo undefined');
        return null;
    }
    if (!sorteoSiguiente || !sorteoSiguiente[KS.num] || !sorteoSiguiente[KS.fecha]) {
        console.error('[extractResultado] FAIL: sorteoSiguiente invalido');
        return null;
    }

    return {
        numSorteo: data[KP.numSorteo],
        resultadoSorteo: parseInt(resultadoSorteo, 10),
        sorteoSiguiente: {
            num: sorteoSiguiente[KS.num],
            fecha: sorteoSiguiente[KS.fecha],
        },
        ts: data[KP.ts],
    };
}

/**
 * Extrae datos de sorteo:cierre_apuestas con validacion fail-fast
 * @param {object} data - Payload del evento
 * @returns {object|null} { numSorteo, sorteoSiguiente, ts } o null si invalido
 */
export function extractCierre(data) {
    const KP = KEYS.EVENT_PAYLOAD;
    const KS = KEYS.SORTEO;

    const numSorteo = data[KP.numSorteo];
    const sorteoSiguiente = data[KP.sorteoSiguiente];

    // Fail-fast
    if (!numSorteo) {
        console.error('[extractCierre] FAIL: numSorteo undefined');
        return null;
    }
    if (!sorteoSiguiente || !sorteoSiguiente[KS.num] || !sorteoSiguiente[KS.fecha]) {
        console.error('[extractCierre] FAIL: sorteoSiguiente invalido');
        return null;
    }

    return {
        numSorteo: numSorteo,
        sorteoSiguiente: {
            num: sorteoSiguiente[KS.num],
            fecha: sorteoSiguiente[KS.fecha],
        },
        ts: data[KP.ts],
    };
}

/**
 * Extrae datos de sorteo:aviso_cierre
 * @param {object} data - Payload del evento
 * @returns {object|null} { numSorteo, ts } o null si invalido
 */
export function extractAviso(data) {
    const KP = KEYS.EVENT_PAYLOAD;

    const numSorteo = data[KP.numSorteo];
    if (!numSorteo) {
        console.error('[extractAviso] FAIL: numSorteo undefined');
        return null;
    }

    return {
        numSorteo: numSorteo,
        ts: data[KP.ts],
    };
}

/**
 * Extrae datos de pozo:update con validacion fail-fast
 * @param {object} data - Payload del evento
 * @returns {object|null} { pozoId, actual } o null si invalido
 */
export function extractPozoUpdate(data) {
    const KP = KEYS.POZO;

    const actual = data[KP.actual];
    if (actual === undefined) {
        console.error('[extractPozoUpdate] FAIL: actual undefined');
        return null;
    }

    return {
        // Un sorteo puede estar suscrito a más de un pozo y recibe un evento por
        // cada uno. Sin el id, el segundo evento pisaba al primero y la card
        // mostraba el pozo de quien llegó último.
        pozoId: data[KP.pozoId] != null ? String(data[KP.pozoId]) : null,
        actual: actual,
    };
}

/**
 * ¿Es este evento de pozo para el producto de ESE juego? El canal es solo el productId, que pueden compartir productos de
 * juegos distintos: el backend marca cada copia con el juego de la suscripción a la que va (`juegoDelCanal`), y con
 * `null` la del canal del propio pozo, que no es de ningún producto. Sin la marca (backend anterior) vale para todos.
 * @param {object} data - Payload del evento
 * @param {string} moduleCode - Código del juego del producto que lo recibe
 */
export function eventoDePozoEsDelJuego(data, moduleCode) {
    const KP = KEYS.POZO;
    if (!data || !Object.prototype.hasOwnProperty.call(data, KP.juegoDelCanal)) return true;
    const juego = data[KP.juegoDelCanal];
    return juego != null && moduleCode != null && String(juego).toLowerCase() === String(moduleCode).toLowerCase();
}

/**
 * Extrae datos de pozo:winner con validacion fail-fast
 * @param {object} data - Payload del evento
 * @returns {object|null} { premio, premioFormateado, nuevoValor } o null si invalido
 */
export function extractPozoWinner(data) {
    const KP = KEYS.POZO;

    const premio = data[KP.premio];
    if (premio === undefined) {
        console.error('[extractPozoWinner] FAIL: premio undefined');
        return null;
    }

    return {
        premio: premio,
        premioFormateado: data[KP.premioFormateado],
        nuevoValor: data[KP.nuevoValor],
    };
}

/**
 * Extrae datos de saldo:update con validacion fail-fast
 * @param {object} data - Payload del evento
 * @returns {object|null} { clienteId, nodoId, nuevoSaldo, tipo, monto } o null si invalido
 */
export function extractSaldoUpdate(data) {
    const KS = KEYS.SALDO;

    const nodoId = data[KS.nodoId];
    const nuevoSaldo = data[KS.nuevoSaldo];

    if (nodoId === undefined || nuevoSaldo === undefined) {
        console.error('[extractSaldoUpdate] FAIL: nodo_id o nuevo_saldo undefined');
        return null;
    }

    return {
        clienteId: data[KS.clienteId],
        nodoId: nodoId,
        nuevoSaldo: nuevoSaldo,
        nuevoSaldoReal: data[KS.nuevoSaldoReal],
        nuevoSaldoBonus: data[KS.nuevoSaldoBonus],
        tipo: data[KS.tipo],
        monto: data[KS.monto],
    };
}

/**
 * Extrae sorteoEnCurso de respuesta /estado
 * @param {object} data - Respuesta del endpoint /estado
 * @returns {object|null} { num, fecha } o null si invalido
 */
export function extractSorteoEnCurso(data) {
    const KE = KEYS.ESTADO;
    const KS = KEYS.SORTEO;

    const sorteo = data[KE.sorteoEnCurso];
    if (!sorteo || !sorteo[KS.num] || !sorteo[KS.fecha]) {
        console.error('[extractSorteoEnCurso] FAIL: sorteoEnCurso invalido');
        return null;
    }

    return {
        num: sorteo[KS.num],
        fecha: sorteo[KS.fecha],
    };
}

/**
 * Extrae sorteoSiguiente de respuesta /estado
 * @param {object} data - Respuesta del endpoint /estado
 * @returns {object|null} { num, fecha } o null si invalido
 */
export function extractSorteoSiguiente(data) {
    const KE = KEYS.ESTADO;
    const KS = KEYS.SORTEO;

    const sorteo = data[KE.sorteoSiguiente];
    if (!sorteo || !sorteo[KS.num] || !sorteo[KS.fecha]) {
        return null; // sorteoSiguiente es opcional en algunos contextos
    }

    return {
        num: sorteo[KS.num],
        fecha: sorteo[KS.fecha],
    };
}

/**
 * Extrae historialResultados de respuesta /estado
 * @param {object} data - Respuesta del endpoint /estado
 * @returns {number[]} Array de numeros o array vacio
 */
export function extractHistorial(data) {
    const KE = KEYS.ESTADO;
    const historial = data[KE.historialResultados];
    return Array.isArray(historial) ? historial : [];
}

// ═══════════════════════════════════════════════════════════════════════════
// NAMESPACE EXPORT
// ═══════════════════════════════════════════════════════════════════════════

export const ApiNomenclature = {
    KEYS,
    EVENTS,
    CHANNELS,
    ENDPOINTS,
    INSTANT_ENDPOINTS,
    INSTANT_RESULT_KIND,
    URL_PARAMS,
    IDIOMAS_SOPORTADOS,
    TV_FLEET_ACTIONS,
    buildSorteoSiguiente,
    buildSorteandoPayload,
    buildResultadoPayload,
    buildAvisoCierrePayload,
    buildCierreApuestasPayload,
    buildPozoResponse,
    buildOkResponse,
    buildErrorResponse,
    extractSorteando,
    extractResultado,
    extractCierre,
    extractAviso,
    extractPozoUpdate,
    extractPozoWinner,
    extractSaldoUpdate,
    extractSorteoEnCurso,
    extractSorteoSiguiente,
    extractHistorial,
};

export default ApiNomenclature;

// ═══════════════════════════════════════════════════════════════════════════
// BACKWARD COMPATIBILITY
// ═══════════════════════════════════════════════════════════════════════════

// Browser global
if (typeof window !== 'undefined') {
    window.ApiNomenclature = ApiNomenclature;
}

// CommonJS
if (typeof module !== 'undefined' && module.exports) {
    module.exports = ApiNomenclature;
}
